import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, FloppyDisk, Buildings, UploadSimple, Trash, WarningOctagon, FilePdf, ArrowRight } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const labelCls = "block text-xs font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1.5";
const inputCls = "flex h-10 w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent";

export default function Settings() {
  const { setSettings: setGlobal } = useAuth();
  const [form, setForm] = useState({ organization_name: "", custom_footer_text: "", logo_url: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [wiping, setWiping] = useState(false);

  const wipe = async () => {
    setWiping(true);
    try {
      await api.delete("/user/account", { data: { confirm: confirmText } });
      toast.success("Your profile and all workspace data were erased.");
      window.location.href = "/login";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erase failed");
      setWiping(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/user/settings");
        setForm(res.data);
      } catch {
        toast.error("Could not load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 600 * 1024) {
      toast.error("Logo must be under 600KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/user/settings", form);
      setForm(res.data);
      setGlobal(res.data);
      toast.success("Branding saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto w-full py-10 px-6 lg:px-10">
        <div className="flex items-center gap-2 text-slate-500">
          <Buildings size={18} weight="duotone" />
          <p className="text-xs font-semibold tracking-[0.12em] uppercase">Workspace Settings</p>
        </div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">White-label Branding</h1>
        <p className="mt-2 text-slate-500">These appear on your printed blueprints and any read-only links you share.</p>

        <div className="mt-8 bg-white border border-slate-200 rounded-sm p-6 space-y-6">
          <div>
            <label className={labelCls}>Organisation Name</label>
            <input data-testid="settings-org-name" value={form.organization_name} onChange={(e) => setForm({ ...form, organization_name: e.target.value })} placeholder="e.g. Acme Operations Group" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Custom Footer Text</label>
            <input data-testid="settings-footer-text" value={form.custom_footer_text} onChange={(e) => setForm({ ...form, custom_footer_text: e.target.value })} placeholder="e.g. Confidential — Internal Distribution Only" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Logo</label>
            <div className="flex items-center gap-4">
              <div className="w-32 h-16 rounded-sm border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                {form.logo_url ? <img src={form.logo_url} alt="" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-slate-300">No logo</span>}
              </div>
              <div className="flex flex-col gap-2">
                <label className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer w-fit">
                  <UploadSimple size={16} /> Upload image
                  <input data-testid="settings-logo-file" type="file" accept="image/*" className="hidden" onChange={onLogoFile} />
                </label>
                {form.logo_url && (
                  <button data-testid="settings-logo-remove" onClick={() => setForm({ ...form, logo_url: "" })} className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 w-fit">
                    <Trash size={14} /> Remove
                  </button>
                )}
              </div>
            </div>
            <input data-testid="settings-logo-url" value={form.logo_url?.startsWith("data:") ? "" : form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="…or paste a secure image URL" className={inputCls + " mt-3"} />
          </div>

          <div className="pt-2">
            <button data-testid="settings-save" onClick={save} disabled={saving}
              className="inline-flex items-center gap-2 rounded-sm bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700">
              {saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />} Save Branding
            </button>
          </div>
        </div>

        {/* Documentation downloads */}
        <div className="mt-10 bg-white border border-slate-200 rounded-sm p-6" data-testid="documentation-card">
          <div className="flex items-center gap-2 text-slate-500">
            <FilePdf size={18} weight="duotone" />
            <p className="text-xs font-semibold tracking-[0.12em] uppercase">Documentation</p>
          </div>
          <h2 className="mt-2 text-xl font-semibold text-slate-900">Platform Reference Documents</h2>
          <p className="mt-2 text-sm text-slate-500 max-w-xl">Polished PDFs you can download, share with stakeholders, or use to onboard new team members.</p>
          <div className="mt-4 space-y-2">
            {[
              { label: "MODUS Design Document", note: "Architecture, pillars, data model & API", href: "/MODUS-Design-Document.pdf" },
              { label: "Tool Compendium", note: "All 21 frameworks explained", href: "/MODUS-Tool-Compendium.pdf" },
              { label: "Design System & Brand Guidelines", note: "Typography, colour, components", href: "/MODUS-Design-System.pdf" },
            ].map((d) => (
              <a
                key={d.href}
                href={d.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`doc-download-${d.href.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase()}`}
                className="group flex items-center gap-3 border border-slate-200 rounded-sm px-4 py-3 hover:border-slate-400 transition-colors"
              >
                <FilePdf size={20} weight="duotone" className="text-indigo-600 shrink-0" />
                <span className="flex-1">
                  <span className="block text-sm font-medium text-slate-900">{d.label}</span>
                  <span className="block text-xs text-slate-500">{d.note}</span>
                </span>
                <ArrowRight size={15} className="text-slate-400 group-hover:translate-x-0.5 transition-transform" />
              </a>
            ))}
          </div>
        </div>

        {/* Danger zone — master wipe kill-switch */}
        <div className="mt-10 bg-white border border-red-300 rounded-sm p-6" data-testid="danger-zone">
          <div className="flex items-center gap-2 text-red-700">
            <WarningOctagon size={18} weight="duotone" />
            <p className="text-xs font-semibold tracking-[0.12em] uppercase">Danger Zone</p>
          </div>
          <h2 className="mt-2 text-xl font-semibold text-slate-900">Erase My Profile &amp; All Workspace Data</h2>
          <p className="mt-2 text-sm text-slate-500 max-w-xl">
            This permanently and irreversibly deletes your profile, authentication history, any subscription state, and every
            document in your workspace. This cannot be undone.
          </p>
          <button
            data-testid="wipe-open-button"
            onClick={() => { setConfirmText(""); setWipeOpen(true); }}
            className="mt-4 inline-flex items-center gap-2 rounded-sm bg-red-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-red-700 transition-colors"
          >
            <Trash size={16} /> Erase everything
          </button>
        </div>
      </div>

      <Dialog open={wipeOpen} onOpenChange={(v) => !wiping && setWipeOpen(v)}>
        <DialogContent data-testid="wipe-modal" className="sm:max-w-md bg-white border border-slate-200 rounded-none p-0 overflow-hidden gap-0">
          <DialogHeader className="px-6 py-5 border-b border-slate-200 bg-red-50 text-left space-y-1">
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-red-700">
              <WarningOctagon size={20} weight="fill" /> Confirm permanent erasure
            </DialogTitle>
          </DialogHeader>
          <div className="p-6 space-y-4">
            <p className="text-sm text-slate-600">
              This action is irreversible. To proceed, type <span className="font-mono font-bold text-slate-900">ERASE</span> below.
            </p>
            <input
              data-testid="wipe-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type ERASE to confirm"
              className="flex h-10 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <div className="flex items-center justify-end gap-2 pt-1">
              <button data-testid="wipe-cancel" onClick={() => setWipeOpen(false)} disabled={wiping}
                className="rounded-sm border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
              <button data-testid="wipe-confirm-button" onClick={wipe} disabled={confirmText.trim().toUpperCase() !== "ERASE" || wiping}
                className="inline-flex items-center gap-2 rounded-sm bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-40">
                {wiping ? <CircleNotch size={16} className="animate-spin" /> : <Trash size={16} />} Erase permanently
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
